// Russian Math Technique Interactive Tutorials
// Tutorial breakdowns for Russian Math techniques with step-by-step practice

import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const russianMathTutorials: Record<string, TechniqueBreakdown> = {
  "0d4e5f6a-b7c8-4d9e-1f2a-4b5c6d7e8f9a": {
    title: "Multiply by 11 Using the Neighbor Rule",
    description:
      "Learn to multiply any number by 11 mentally using the simple neighbor addition method!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding the Neighbor Rule",
        explanation:
          "Add each digit to its right neighbor (the digit to its right). Treat the number as having a leading zero.",
        practice: {
          problem: "25 × 11",
          question: "What is the ones digit? (Add 5 + 0)",
          answer: "5",
          hint: "The rightmost digit has no neighbor, so add 0",
        },
      },
      {
        stepNumber: 2,
        title: "Working Right to Left",
        explanation:
          "Start from the right, add each digit to its neighbor, write the units digit and carry any tens.",
        practice: {
          problem: "25 × 11",
          question: "What is the tens digit? (Add 2 + 5)",
          answer: "7",
          hint: "2 + 5 = 7, no carry needed",
          intermediateSteps: [
            { label: "Ones place", answer: "5", hint: "5 + 0 = 5" },
            { label: "Tens place", answer: "7", hint: "2 + 5 = 7" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Handling the Leading Digit",
        explanation:
          "The leftmost digit just adds 0 (or the carry from the previous step).",
        practice: {
          problem: "25 × 11",
          question: "What is the hundreds digit? (Add 0 + 2)",
          answer: "2",
          hint: "Leading zero + 2 = 2",
          intermediateSteps: [
            { label: "Complete answer", answer: "275", hint: "25 × 11 = 275" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Practice with Carries",
        explanation:
          "When the sum of neighbors ≥ 10, write the units digit and carry the tens digit to the next position.",
        practice: {
          problem: "67 × 11",
          question: "Calculate 67 × 11 step by step",
          answer: "737",
          hint: "Ones: 7, Tens: 6+7=13→3 carry 1, Hundreds: 0+6+1=7",
          intermediateSteps: [
            { label: "Ones", answer: "7", hint: "7 + 0 = 7" },
            {
              label: "Tens",
              answer: "3",
              hint: "6 + 7 = 13, write 3, carry 1",
            },
            { label: "Hundreds", answer: "7", hint: "0 + 6 + 1(carry) = 7" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Large Numbers",
        explanation:
          "The method works for any size number! Just keep adding neighbors from right to left.",
        practice: {
          problem: "3425 × 11",
          question: "What is 3425 × 11?",
          answer: "37675",
          hint: "Work right to left: 5, 2+5=7, 4+2=6, 3+4=7, 0+3=3",
          intermediateSteps: [
            {
              label: "Step by step",
              answer: "37675",
              hint: "5→7→6→7→3 = 37675",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "3425 × 11",
      fullSolution: "37675 (Neighbors: 5+0=5, 2+5=7, 4+2=6, 3+4=7, 0+3=3)",
    },
  },

  "3a7b8c9d-e0f1-4a2b-4c5d-7e8f9a0b1c2d": {
    title: "Mental Subtraction Without Borrowing",
    description:
      "Subtract by place value separately, allowing negative intermediate results!",
    steps: [
      {
        stepNumber: 1,
        title: "Partition Both Numbers",
        explanation:
          "Break each number into place values: tens and ones separately.",
        practice: {
          problem: "61 - 17",
          question: "How do you partition 61 and 17?",
          answer: "61 = 60 + 1, 17 = 10 + 7",
          hint: "Separate the tens place from the ones place",
        },
      },
      {
        stepNumber: 2,
        title: "Subtract Each Place Separately",
        explanation:
          "Subtract tens from tens, ones from ones. Negative results are OK!",
        practice: {
          problem: "61 - 17",
          question: "Calculate: (60 - 10) and (1 - 7)",
          answer: "50 and -6",
          hint: "Tens: 60-10=50, Ones: 1-7=-6 (negative is fine!)",
          intermediateSteps: [
            { label: "Tens place", answer: "50", hint: "60 - 10 = 50" },
            { label: "Ones place", answer: "-6", hint: "1 - 7 = -6" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Combine the Results",
        explanation:
          "Add the place value results together: positive + negative = final answer.",
        practice: {
          problem: "61 - 17",
          question: "Combine: 50 + (-6) = ?",
          answer: "44",
          hint: "50 - 6 = 44",
        },
      },
      {
        stepNumber: 4,
        title: "Three-Digit Example",
        explanation:
          "The same method works for larger numbers with multiple place values.",
        practice: {
          problem: "342 - 157",
          question: "Partition and subtract: 342 - 157",
          answer: "185",
          hint: "Hundreds: 300-100=200, Tens: 40-50=-10, Ones: 2-7=-5, Total: 200-10-5=185",
          intermediateSteps: [
            { label: "Hundreds", answer: "200", hint: "300 - 100 = 200" },
            { label: "Tens", answer: "-10", hint: "40 - 50 = -10" },
            { label: "Ones", answer: "-5", hint: "2 - 7 = -5" },
            {
              label: "Combine",
              answer: "185",
              hint: "200 + (-10) + (-5) = 185",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "342 - 157",
      fullSolution:
        "185 (Partition: 200 + (-10) + (-5) = 185, no borrowing needed!)",
    },
  },

  "4b8c9d0e-f1a2-4b3c-5d6e-8f9a0b1c2d3e": {
    title: "Instant Number Recognition with Russian Abacus",
    description:
      "Learn to instantly recognize quantities using the 4-2-4 bead pattern!",
    steps: [
      {
        stepNumber: 1,
        title: "The 4-2-4 Pattern",
        explanation:
          "Russian abacus has 10 beads per wire: [4 dark][2 light][4 dark]. The light beads create a visual anchor at 5 and 6.",
        practice: {
          problem: "Recognize pattern [●●●●][◐◐][○○○○]",
          question: "How many beads are pushed left (active)?",
          answer: "6",
          hint: "4 dark beads + 2 light beads = 6",
        },
      },
      {
        stepNumber: 2,
        title: "Subitizing Small Numbers (1-4)",
        explanation:
          "Your brain can instantly recognize 1-4 objects without counting. Use the dark bead group.",
        practice: {
          problem: "Pattern: [●●●○][○○][○○○○]",
          question: "How many beads are active?",
          answer: "3",
          hint: "Just see the pattern: 3 dark beads pushed left = 3",
        },
      },
      {
        stepNumber: 3,
        title: "Using the 5-6 Anchor",
        explanation:
          'The 2 light beads create a "wall" at 6. All beads through the light section = 6.',
        practice: {
          problem: "Pattern: [●●●●][◐○][○○○○]",
          question: "What number does this show?",
          answer: "5",
          hint: "4 dark + 1 light = 5",
          intermediateSteps: [
            { label: "Dark beads", answer: "4", hint: "4 beads pushed left" },
            { label: "Light beads", answer: "1", hint: "1 light bead pushed" },
            { label: "Total", answer: "5", hint: "4 + 1 = 5" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Numbers 7-10",
        explanation:
          "See the 6-pattern (all left beads + both light), then count the additional dark beads on the right.",
        practice: {
          problem: "Pattern: [●●●●][◐◐][●●●○]",
          question: "What number is shown?",
          answer: "9",
          hint: "See the 6-wall, then add 3 more dark beads: 6 + 3 = 9",
          intermediateSteps: [
            { label: "6-wall pattern", answer: "6", hint: "[●●●●][◐◐] = 6" },
            {
              label: "Additional beads",
              answer: "3",
              hint: "Plus 3 dark on right",
            },
            { label: "Total", answer: "9", hint: "6 + 3 = 9" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Instant Recognition Practice",
        explanation:
          "With practice, you recognize all patterns instantly without counting!",
        practice: {
          problem: "Flash recognition: [●●●●][◐◐][●●○○]",
          question: "What number? (Answer as fast as possible!)",
          answer: "8",
          hint: "6-wall + 2 more = 8 (instant!)",
        },
      },
    ],
    finalExample: {
      problem: "[●●●●][◐◐][●●●○]",
      fullSolution:
        "9 (See 6-wall + 3 beads = 9, recognized in under 0.5 seconds!)",
    },
  },

  "8b2c3d4e-f5a6-4b7c-9d0e-2f3a4b5c6d7e": {
    title: "Division by Doubling and Greedy Subtraction",
    description:
      "Systematic division using doubling tables instead of trial-and-error!",
    steps: [
      {
        stepNumber: 1,
        title: "Build the Doubling Table",
        explanation:
          "Start with 1× divisor, keep doubling until the next double would exceed the dividend.",
        practice: {
          problem: "45 ÷ 6",
          question: "Build doubling table for divisor 6 up to 45",
          answer: "1→6, 2→12, 4→24, 8→48(stop)",
          hint: "Double: 6, 12, 24, 48. Stop at 48 since 48 > 45",
          intermediateSteps: [
            { label: "1 × 6", answer: "6" },
            { label: "2 × 6", answer: "12" },
            { label: "4 × 6", answer: "24" },
            { label: "8 × 6", answer: "48", hint: "Stop! 48 > 45" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Greedy Subtraction",
        explanation:
          "Pick the LARGEST value ≤ dividend, subtract it, track its multiplier.",
        practice: {
          problem: "45 ÷ 6 (table: 6,12,24,48)",
          question: "First greedy pick from 45?",
          answer: "24 (multiplier 4)",
          hint: "Largest value ≤ 45 is 24, with multiplier 4",
        },
      },
      {
        stepNumber: 3,
        title: "Continue Until Done",
        explanation: "Repeat greedy subtraction until remainder < divisor.",
        practice: {
          problem: "45 ÷ 6 (after picking 24)",
          question: "Continue: 45-24=21, what next?",
          answer: "12 (multiplier 2), then 6 (multiplier 1)",
          hint: "21-12=9, then 9-6=3. Stop since 3<6",
          intermediateSteps: [
            {
              label: "45 - 24",
              answer: "21",
              hint: "Remainder: 21, multiplier: 4",
            },
            {
              label: "21 - 12",
              answer: "9",
              hint: "Remainder: 9, multiplier: 2",
            },
            {
              label: "9 - 6",
              answer: "3",
              hint: "Remainder: 3<6, multiplier: 1, DONE!",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Sum the Multipliers",
        explanation: "Add all the multipliers you used: that's your quotient!",
        practice: {
          problem: "45 ÷ 6 (used: 4, 2, 1)",
          question: "Sum multipliers: 4 + 2 + 1 = ?",
          answer: "7",
          hint: "Quotient = 7, Remainder = 3",
          intermediateSteps: [
            { label: "Quotient", answer: "7", hint: "4 + 2 + 1 = 7" },
            { label: "Remainder", answer: "3", hint: "Final remainder" },
            { label: "Answer", answer: "7 R 3", hint: "45 ÷ 6 = 7 R 3" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "100 ÷ 7",
      fullSolution:
        "14 R 2 (Table: 7,14,28,56,112. Greedy: 56(8)+28(4)+14(2)=98. Quotient: 8+4+2=14, R: 2)",
    },
  },

  "5c9d0e1f-a2b3-4c4d-6e7f-9a0b1c2d3e4f": {
    title: "Logical Reasoning Without Computation",
    description:
      "Solve problems by analyzing relationships instead of calculating!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify the Relationship",
        explanation:
          "Given an equation, focus on HOW values relate, not the numbers themselves.",
        practice: {
          problem: "If 635 - 182 = 453, find 635 - 181",
          question: "What changed from the original equation?",
          answer: "Subtrahend decreased by 1",
          hint: "182 → 181, so we're subtracting 1 less",
        },
      },
      {
        stepNumber: 2,
        title: "Apply Logical Rules",
        explanation:
          "If you subtract LESS, you get MORE. If you subtract MORE, you get LESS.",
        practice: {
          problem: "If 635 - 182 = 453, find 635 - 181",
          question: "Since we subtract 1 less, how does the answer change?",
          answer: "Answer increases by 1",
          hint: "Subtracting less → result is more",
        },
      },
      {
        stepNumber: 3,
        title: "Calculate the New Answer",
        explanation: "Adjust the known answer by the logical relationship.",
        practice: {
          problem: "If 635 - 182 = 453, find 635 - 181",
          question: "What is 453 + 1?",
          answer: "454",
          hint: "Original answer 453, increase by 1 = 454",
          intermediateSteps: [
            { label: "Original", answer: "453", hint: "635 - 182 = 453" },
            { label: "Change", answer: "+1", hint: "Subtracting 1 less" },
            { label: "New answer", answer: "454", hint: "453 + 1 = 454" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Multiple Changes",
        explanation:
          "Track how BOTH values change and determine the NET effect.",
        practice: {
          problem: "If 428 + 357 = 785, find 430 + 355",
          question: "What is the net change?",
          answer: "+2 and -2, net = 0",
          hint: "First addend +2, second addend -2, cancel out!",
          intermediateSteps: [
            { label: "First change", answer: "+2", hint: "428 → 430" },
            { label: "Second change", answer: "-2", hint: "357 → 355" },
            { label: "Net effect", answer: "0", hint: "+2 - 2 = 0" },
            { label: "Answer", answer: "785", hint: "785 + 0 = 785" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Pattern Recognition",
        explanation:
          "Identify patterns like doubling, halving, or algebraic equivalence.",
        practice: {
          problem: "If 15 × 8 = 120, find 30 × 4",
          question: "How do 30 × 4 relate to 15 × 8?",
          answer: "First doubled, second halved",
          hint: "30 = 15×2, 4 = 8÷2, so answer stays the same: 120",
        },
      },
    ],
    finalExample: {
      problem: "If 635 - 182 = 453, find 635 - 181",
      fullSolution:
        "454 (Logical: subtracting 1 less → answer is 1 more: 453 + 1 = 454)",
    },
  },

  "6d0e1f2a-b3c4-4d5e-7f8a-0b1c2d3e4f4b": {
    title: "Mental Multiplication Using Units-Tens Pairs",
    description: "Multiply large numbers without writing partial products!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding UT-Pairs",
        explanation:
          "Every single-digit product has a Units part (U) and Tens part (T). Example: 7×8=56 → U=6, T=5.",
        practice: {
          problem: "23 × 17: Calculate ones place",
          question: "Multiply rightmost digits: 3 × 7 = ?",
          answer: "21, so U=1, T=2",
          hint: "Write 1 in ones place, carry 2 to tens",
        },
      },
      {
        stepNumber: 2,
        title: "Cross-Product Pattern",
        explanation:
          "For tens place, calculate ALL cross products: (A×D) + (B×C) + carry.",
        practice: {
          problem: "23 × 17: Calculate tens place",
          question: "Calculate (2×7) + (3×1) + carry(2)",
          answer: "14 + 3 + 2 = 19, write 9, carry 1",
          hint: "Cross products plus previous carry",
          intermediateSteps: [
            { label: "2 × 7", answer: "14" },
            { label: "3 × 1", answer: "3" },
            { label: "Plus carry", answer: "2" },
            { label: "Total", answer: "19", hint: "Write 9, carry 1" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Final Position",
        explanation:
          "For hundreds place (2-digit × 2-digit), just multiply leftmost digits plus carry.",
        practice: {
          problem: "23 × 17: Calculate hundreds place",
          question: "Calculate 2 × 1 + carry(1)",
          answer: "3",
          hint: "2 + 1 = 3",
          intermediateSteps: [
            { label: "2 × 1", answer: "2" },
            { label: "Plus carry", answer: "1" },
            { label: "Final answer", answer: "391", hint: "23 × 17 = 391" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Practice with Larger Numbers",
        explanation: "The same pattern works for any size multiplication!",
        practice: {
          problem: "45 × 38",
          question: "Calculate 45 × 38 using UT-Pairs",
          answer: "1710",
          hint: "Ones: 5×8=40→0,carry 4. Tens: (4×8)+(5×3)+4=32+15+4=51→1,carry 5. Hundreds: (4×3)+5=17→1710",
          intermediateSteps: [
            { label: "Ones", answer: "0", hint: "5×8=40, write 0, carry 4" },
            {
              label: "Tens",
              answer: "1",
              hint: "32+15+4=51, write 1, carry 5",
            },
            { label: "Hundreds", answer: "17", hint: "12+5=17, answer: 1710" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "45 × 38",
      fullSolution:
        "1710 (UT-Pairs: ones=0(c4), tens=1(c5), hundreds=17, direct computation!)",
    },
  },

  "9c3d4e5f-a6b7-4c8d-0e1f-3f4a5b6c7d8e": {
    title: "Visual Polynomial Inequality Solving",
    description:
      "Solve inequalities by drawing a snake that crosses and bounces at roots!",
    steps: [
      {
        stepNumber: 1,
        title: "Find Roots and Multiplicities",
        explanation:
          "Factor completely and identify each root's multiplicity: odd = cross, even = bounce.",
        practice: {
          problem: "Solve (x-1)(x+2)² > 0",
          question: "Identify roots and their multiplicities",
          answer: "x=1 (mult 1, odd→cross), x=-2 (mult 2, even→bounce)",
          hint: "Count the exponent on each factor",
        },
      },
      {
        stepNumber: 2,
        title: "Draw Number Line",
        explanation: "Plot all roots on a number line from left to right.",
        practice: {
          problem: "(x-1)(x+2)² > 0",
          question: "Plot roots: -2 and 1",
          answer: "---●(-2)-------●(1)---",
          hint: "Mark -2 and 1 on the line",
        },
      },
      {
        stepNumber: 3,
        title: "Determine Starting Position",
        explanation:
          "Check leading coefficient: positive → start top-right, negative → start bottom-right.",
        practice: {
          problem: "(x-1)(x+2)² > 0",
          question: "What is the leading coefficient?",
          answer: "Positive (+1), start TOP-RIGHT",
          hint: "Expand: x³+... so leading coeff is +1",
        },
      },
      {
        stepNumber: 4,
        title: "Draw the Snake",
        explanation:
          "From right to left: CROSS at odd roots, BOUNCE at even roots.",
        practice: {
          problem: "(x-1)(x+2)² > 0",
          question: "Trace snake from right to left",
          answer:
            "Start above at x>1, cross at x=1 (go below), bounce at x=-2 (stay below)",
          hint: "Top-right → cross down at 1 → bounce at -2 (stay below)",
          intermediateSteps: [
            { label: "Right of 1", answer: "Above axis (+)" },
            { label: "At x=1", answer: "CROSS to below" },
            { label: "Between -2 and 1", answer: "Below axis (-)" },
            { label: "At x=-2", answer: "BOUNCE (stay below)" },
            { label: "Left of -2", answer: "Below axis (-)" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Read the Solution",
        explanation:
          "For > 0, take intervals where snake is ABOVE axis. For < 0, take BELOW.",
        practice: {
          problem: "(x-1)(x+2)² > 0",
          question: "Which intervals have snake above axis?",
          answer: "Only (1, ∞)",
          hint: "Snake is above only when x > 1",
          intermediateSteps: [
            { label: "Solution", answer: "x ∈ (1, ∞)", hint: "Final answer!" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "(x-1)(x+2)² > 0",
      fullSolution:
        "x ∈ (1, ∞) (Snake: top-right → cross at 1 → bounce at -2, only above for x>1)",
    },
  },

  "1e5f6a7b-c8d9-4e0f-2a3b-5c6d7e8f9a0b": {
    title: "Proving Impossibility Through Invariants",
    description:
      "Find properties that stay constant to prove a state is unreachable!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify the Invariant",
        explanation:
          "Look for a property that EVERY operation preserves (parity, modulo, coloring, etc.).",
        practice: {
          problem:
            "15 black, 10 white stones. Rule: remove 2 same→add 1 opposite, remove different→add 1 black. Final stone color?",
          question: "What property is preserved?",
          answer: "Parity of black stones (B mod 2)",
          hint: "Check: BB→W (B-2), WW→B (B+1), BW→B (B same). Only WW changes parity!",
        },
      },
      {
        stepNumber: 2,
        title: "Verify Invariant Holds",
        explanation:
          "Prove that ALL allowed operations preserve the invariant.",
        practice: {
          problem: "Stone game invariant: B mod 2",
          question: "Show BB→W preserves parity",
          answer: "B → B-2, same parity",
          hint: "Removing 2 keeps parity: even→even, odd→odd",
          intermediateSteps: [
            { label: "BB→W", answer: "B-2 (parity preserved)" },
            { label: "WW→B", answer: "B+1 (parity flips)" },
            { label: "BW→B", answer: "B (parity preserved)" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Calculate Initial State",
        explanation: "Determine the invariant value at the start.",
        practice: {
          problem: "Stone game: 15 black, 10 white",
          question: "What is B mod 2 initially?",
          answer: "15 mod 2 = 1 (odd)",
          hint: "15 black stones → odd parity",
        },
      },
      {
        stepNumber: 4,
        title: "Calculate Target State",
        explanation: "Determine the invariant value for the target state.",
        practice: {
          problem: "Stone game: 1 stone remains",
          question: "What are possible final parities?",
          answer: "1B (odd) or 0B (even)",
          hint: "If 1 black remains: parity=1. If 1 white: parity=0",
        },
      },
      {
        stepNumber: 5,
        title: "Compare and Conclude",
        explanation:
          "If invariants differ → impossible. If same → might be possible.",
        practice: {
          problem: "Stone game final stone",
          question: "What color is the final stone?",
          answer: "BLACK (parity must stay odd)",
          hint: "Initial: odd. Final must be: odd → 1 black stone",
          intermediateSteps: [
            { label: "Initial parity", answer: "odd (15 black)" },
            { label: "Preserved by operations", answer: "stays odd" },
            {
              label: "Final stone",
              answer: "BLACK",
              hint: "1 black = odd parity ✓",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Can knight reach same-color square in odd moves?",
      fullSolution:
        "NO (Invariant: color parity alternates each move, odd moves → different color!)",
    },
  },

  "2f6a7b8c-d9e0-4f1a-3b4c-6d7e8f9a0b1c": {
    title: "Solving by Analyzing Extreme Cases",
    description:
      "Focus on minimum, maximum, or boundary elements to solve seemingly complex problems!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify the Extremal Element",
        explanation:
          "Choose what to minimize/maximize: smallest distance, largest value, first/last element, etc.",
        practice: {
          problem:
            "100 people in a room. Prove at least 2 have the same number of friends.",
          question: "What extremal elements to consider?",
          answer: "Person with MOST friends and person with FEWEST friends",
          hint: "Focus on the min and max friend counts",
        },
      },
      {
        stepNumber: 2,
        title: "Analyze Extremal Properties",
        explanation: "What unique constraints does the extremal element have?",
        practice: {
          problem: "100 people, prove 2 have same friend count",
          question: "What are the possible friend counts?",
          answer: "0, 1, 2, ..., 99 (but not all possible!)",
          hint: "Min: 0 friends, Max: 99 friends",
          intermediateSteps: [
            {
              label: "Min friends",
              answer: "0",
              hint: "Someone could know nobody",
            },
            {
              label: "Max friends",
              answer: "99",
              hint: "Someone could know everyone else",
            },
            { label: "Possible values", answer: "100 different values (0-99)" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Find the Contradiction",
        explanation:
          "The extremal property often leads to a contradiction or forced conclusion.",
        practice: {
          problem: "100 people, possible friend counts 0-99",
          question: "Can someone have 0 friends AND someone have 99 friends?",
          answer: "NO! Contradiction",
          hint: "If someone has 99 friends (knows everyone), nobody can have 0 friends!",
          intermediateSteps: [
            { label: "If 0 friends exists", answer: "Someone knows nobody" },
            {
              label: "If 99 friends exists",
              answer: "Someone knows EVERYONE (including 0-friend person)",
            },
            { label: "Contradiction!", answer: "0 and 99 cannot both exist" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Apply Pigeonhole Principle",
        explanation:
          'With extremal contradiction, we have fewer "holes" than "pigeons".',
        practice: {
          problem: "100 people, at most 99 possible friend counts",
          question: "What does Pigeonhole Principle tell us?",
          answer: "At least 2 people must have the same count",
          hint: "100 people, only 99 possible values → at least 2 share a value!",
          intermediateSteps: [
            { label: "People (pigeons)", answer: "100" },
            { label: "Possible values (holes)", answer: "99 (0-98 OR 1-99)" },
            {
              label: "Conclusion",
              answer: "At least 2 people have same friend count ✓",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "5 points, all triangles area ≥1. Min distance?",
      fullSolution:
        "≥1.52 (Pick CLOSEST pair as extremal, derive constraints from triangle areas)",
    },
  },

  "7e8f9a0b-1c2d-3e4f-5a6b-7c8d9e0f1a2b": {
    title: "Systematic Grid Toggle Puzzle Solving",
    description:
      "Solve light puzzles by chasing from top to bottom systematically!",
    steps: [
      {
        stepNumber: 1,
        title: "Fix Row 1",
        explanation:
          "Choose which lights in row 1 to press (or try all 2^n combinations).",
        practice: {
          problem: "3×3 lights puzzle, all ON",
          question: "What is the first step?",
          answer: "Choose row 1 pattern (start with no presses)",
          hint: "For 3×3, we have 2³=8 possible row 1 patterns to try",
        },
      },
      {
        stepNumber: 2,
        title: "Chase Row 1 into Row 2",
        explanation:
          "For each ON light in row 1, press the button directly below it in row 2.",
        practice: {
          problem: "3×3 grid, row 1: [ON,ON,OFF]",
          question: "Which row 2 buttons to press?",
          answer: "Press column 1 and column 2",
          hint: "Press below each ON light in row 1",
          intermediateSteps: [
            { label: "Row 1 position 1", answer: "ON → press [2,1]" },
            { label: "Row 1 position 2", answer: "ON → press [2,2]" },
            { label: "Row 1 position 3", answer: "OFF → skip" },
            { label: "After row 2 presses", answer: "Row 1 now all OFF!" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Chase Row 2 into Row 3",
        explanation:
          "Same process: press row 3 buttons below each ON light in row 2.",
        practice: {
          problem: "After chasing row 1, row 2 is [OFF,ON,ON]",
          question: "Which row 3 buttons to press?",
          answer: "Press [3,2] and [3,3]",
          hint: "Press below the ON lights in row 2",
        },
      },
      {
        stepNumber: 4,
        title: "Check Final Row",
        explanation:
          "After chasing all rows, check if the last row is all OFF.",
        practice: {
          problem: "After all chasing, row 3 is [ON,OFF,ON]",
          question: "Is this configuration solved?",
          answer: "NO, not all lights OFF",
          hint: "If final row has any ON lights, try different row 1 pattern",
          intermediateSteps: [
            { label: "Row 1", answer: "All OFF (chased)" },
            { label: "Row 2", answer: "All OFF (chased)" },
            { label: "Row 3", answer: "Has ON lights → NOT SOLVED" },
            {
              label: "Next step",
              answer: "Try different row 1 initial pattern",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Try All Row 1 Patterns",
        explanation:
          "If one pattern doesn't work, systematically try all 2^n possibilities.",
        practice: {
          problem: "3×3 grid puzzle",
          question: "How many row 1 patterns to try at most?",
          answer: "8 patterns (2³)",
          hint: "For width n, try up to 2^n row 1 combinations",
          intermediateSteps: [
            { label: "Grid width", answer: "3 columns" },
            { label: "Possible patterns", answer: "2³ = 8" },
            {
              label: "Strategy",
              answer: "Try each, one will work (if solvable)",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "3×3 all ON grid",
      fullSolution:
        "Solved by chasing with specific row 1 pattern (deterministic, no guessing!)",
    },
  },

  "8f9a0b1c-2d3e-4f5a-6b7c-8d9e0f1a2b3c": {
    title: "Reachability in Step-by-Step Systems",
    description: "Prove which positions MUST be visited in ±1 step systems!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify Constraints",
        explanation:
          "Note starting position, ending position, number of steps, and step size.",
        practice: {
          problem: "Token at 0, moves ±1, ends at 3 after 15 steps",
          question: "What are the constraints?",
          answer: "Start=0, End=3, Steps=15, Move=±1",
          hint: "List all the given information",
        },
      },
      {
        stepNumber: 2,
        title: "Check Parity First",
        explanation:
          "For ±1 steps: position parity alternates. Check if end position has correct parity.",
        practice: {
          problem: "Start=0, Steps=15, End=3",
          question: "Is ending parity correct?",
          answer: "YES (0 is even, 15 steps → odd position, 3 is odd ✓)",
          hint: "After odd steps from even start → must end on odd",
          intermediateSteps: [
            { label: "Start parity", answer: "even (0)" },
            { label: "Steps parity", answer: "odd (15)" },
            { label: "End parity must be", answer: "odd" },
            { label: "End position", answer: "3 (odd) ✓" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Calculate Maximum Reach",
        explanation:
          "Determine the furthest position that could have been reached.",
        practice: {
          problem: "Start=0, Steps=15, End=3",
          question: "What is maximum position reachable?",
          answer: "9",
          hint: "Formula: (steps + |displacement|)/2 = (15+3)/2 = 9",
          intermediateSteps: [
            { label: "Total steps", answer: "15" },
            { label: "Net displacement", answer: "3" },
            { label: "Max position", answer: "(15+3)/2 = 9" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Apply Discrete Continuity",
        explanation:
          "With ±1 steps, you MUST pass through every integer between min and max positions.",
        practice: {
          problem: "Max reached: 9, End: 3",
          question: "Did token reach position 7?",
          answer: "YES (7 is between 0 and 9)",
          hint: "Discrete continuity: must hit all integers from 0 to 9",
          intermediateSteps: [
            { label: "Min position", answer: "0 (or negative)" },
            { label: "Max position", answer: "9" },
            { label: "Target: 7", answer: "Between 0 and 9" },
            { label: "Conclusion", answer: "MUST have hit position 7 ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Start=0, ±1 steps, End=3 in 15 steps, reach 7?",
      fullSolution:
        "YES (Max reach=9, 7 is between 0 and 9, discrete continuity guarantees it!)",
    },
  },

  "9a0b1c2d-3e4f-5a6b-7c8d-9e0f1a2b3c4d": {
    title: "Proving Termination Through Monotonic Decrease",
    description:
      "Find quantities that always decrease to prove a process must end!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify the Process",
        explanation:
          "Understand what operations are allowed and whether it could run forever.",
        practice: {
          problem:
            "Numbers [3,5,7]. Operation: pick 2, replace with their sum. Can this repeat forever?",
          question: "What happens to the count of numbers?",
          answer: "Decreases by 1 each operation",
          hint: "Start: 3 numbers. After operation: 2 numbers",
        },
      },
      {
        stepNumber: 2,
        title: "Choose a Monovariant",
        explanation:
          "Find a quantity that ALWAYS decreases (or increases) with each operation.",
        practice: {
          problem: "Combining numbers game",
          question: "What is a good monovariant?",
          answer: "M = number of elements",
          hint: "Count goes: 3 → 2 → 1 (always decreasing)",
          intermediateSteps: [
            { label: "Initial", answer: "M = 3" },
            { label: "After 1 op", answer: "M = 2 (decreased by 1)" },
            { label: "After 2 ops", answer: "M = 1 (decreased by 1)" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Verify Monotonicity",
        explanation:
          "Prove that EVERY possible operation decreases (or increases) the monovariant.",
        practice: {
          problem: "Combining numbers: M = count",
          question: "Does count always decrease?",
          answer: "YES, every operation: remove 2, add 1 → net -1",
          hint: "No matter which 2 numbers picked, count always drops by 1",
        },
      },
      {
        stepNumber: 4,
        title: "Check Boundedness",
        explanation:
          "The monovariant must have a lower (or upper) bound it cannot cross.",
        practice: {
          problem: "Combining numbers: M = count",
          question: "What is the lower bound for M?",
          answer: "1 (must have at least 1 number)",
          hint: "Cannot go below 1 number",
          intermediateSteps: [
            { label: "M starts at", answer: "3" },
            { label: "M decreases by", answer: "1 per operation" },
            { label: "M is bounded by", answer: "≥ 1" },
            { label: "Path", answer: "3 → 2 → 1 → STOP" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Conclude Termination",
        explanation: "Bounded + monotonic → must terminate in finite steps!",
        practice: {
          problem: "Combining numbers with M=3",
          question: "How many operations until termination?",
          answer: "Exactly 2 operations",
          hint: "M goes from 3 to 1: decrease of 2, so 2 operations",
          intermediateSteps: [
            { label: "Initial state", answer: "3 numbers (M=3)" },
            { label: "After 1 op", answer: "2 numbers (M=2)" },
            { label: "After 2 ops", answer: "1 number (M=1) DONE!" },
            { label: "Max operations", answer: "2 (process MUST terminate)" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "100 chips to split into singles, cost=pile size per split",
      fullSolution:
        "Total cost = 4950 (Monovariant: sum of squares, decreases from 10000 to 100)",
    },
  },
};
